// „От минала доставка" — селектът на хедъра на доставката: двупанелен попъп с
// доставките на избрания доставчик вляво и продуктите на посочената вдясно.
// Прилагането пренаписва редовете на листа (виж applyTemplate в страницата).
import { useState } from "react";
import { Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery, Product } from "../../../../api/inventory";
import {
  formatPrice,
  formatQtyInput,
  formatUnitPrice,
} from "./deliveryWorksheetShared";

// ─── „От минала доставка": двупанелен попъп ─────────────────────────────────
// Вляво компактен списък с доставките на доставчика, вдясно preview на
// посочената с hover доставка като мини таблица.

// колони на preview таблицата: продукт · количество · ед. цена
const PREVIEW_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_130px_84px] gap-2.5";

export const DeliveryTemplateSelect = ({
  templateId,
  supplierDeliveries,
  loading,
  productById,
  decimalsOf,
  onApply,
  onClear,
}: {
  templateId: number | null;
  supplierDeliveries: Delivery[];
  loading: boolean;
  productById: Map<number, Product>;
  decimalsOf: (productId: number) => number;
  onApply: (deliveryId: number) => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  // Доставката, чиито продукти се показват в дясната част на попъпа
  // (при hover върху опция)
  const [previewId, setPreviewId] = useState<number | null>(null);

  // Сбито резюме на доставка за preview-то: редовете, групирани по продукт
  // (разпределена доставка = 1 запис), в базови единици
  const templateGoods = (delivery: Delivery) => {
    const byProduct = new Map<
      number,
      { name: string; qty: number; price: number }
    >();
    for (const line of delivery.lines) {
      const current = byProduct.get(line.productId);
      if (current) current.qty += line.quantity;
      else
        byProduct.set(line.productId, {
          name: line.productName,
          qty: line.quantity,
          price: line.unitPrice,
        });
    }
    return [...byProduct.entries()].map(([productId, goods]) => ({
      productId,
      ...goods,
      unit: productById.get(productId)?.unit ?? "",
    }));
  };

  return (
    <Select
      value={templateId ?? undefined}
      onChange={(v) => {
        if (v == null) onClear();
        else onApply(v as number);
      }}
      allowClear
      placeholder={t("inventoryPage.fieldTemplatePh")}
      loading={loading}
      popupMatchSelectWidth={false}
      listHeight={330}
      // antd вътрешен попъп — конфигурира се само през styles проп-а
      styles={{ popup: { root: { padding: 0, overflow: "hidden" } } }}
      onOpenChange={(open) => {
        if (!open) setPreviewId(null);
      }}
      options={supplierDeliveries.map((delivery) => ({
        value: delivery.id,
        title: "",
        label:
          `${dayjs(delivery.date).format("DD.MM.YYYY")} · ` +
          t("inventoryPage.templateOptionProducts", {
            count: delivery.productsCount,
          }) +
          ` · ${formatPrice(delivery.totalValueWithVat)}` +
          (delivery.invoiceNumber ? ` · №${delivery.invoiceNumber}` : ""),
        delivery,
      }))}
      // Лява колона: компактен ред за доставка; hover → preview вдясно
      // (onMouseEnter тук е ФУНКЦИОНАЛЕН — избира preview, не стилизира)
      optionRender={(option) => {
        const delivery = (option.data as { delivery: Delivery }).delivery;
        return (
          <div
            onMouseEnter={() => setPreviewId(delivery.id)}
            className="flex items-baseline gap-2 py-0.5"
          >
            <span className="text-[13px] font-bold leading-[normal] text-ink">
              {dayjs(delivery.date).format("DD.MM.YYYY")}
            </span>
            <span className="text-[11.5px] leading-[normal] text-muted">
              {t("inventoryPage.templateOptionProducts", {
                count: delivery.productsCount,
              })}
            </span>
            <span className="ml-auto font-[ui-monospace,Menlo,monospace] text-[12.5px] font-semibold leading-[normal] text-brand-deep-ink">
              {formatPrice(delivery.totalValueWithVat)}
            </span>
          </div>
        );
      }}
      // Двупанелен попъп: списък вляво + продуктите на посочената доставка
      // като мини таблица вдясно
      popupRender={(menu) => {
        const previewDelivery =
          supplierDeliveries.find((d) => d.id === previewId) ??
          supplierDeliveries[0];
        const goods = previewDelivery ? templateGoods(previewDelivery) : [];
        return (
          <div className="flex items-stretch">
            {/* Лява релса: списъкът с доставки — по-тясна, с топъл фон,
                за да не „зее" бяло под кратък списък */}
            <div className="flex-[0_0_250px] border-r border-shade-2 bg-fill p-1">
              {menu}
            </div>
            <div className="flex max-h-[330px] min-w-0 flex-[0_0_430px] flex-col">
              {previewDelivery && (
                <>
                  <div className="flex shrink-0 items-baseline gap-2.5 border-b border-shade-2 px-3.5 pb-[7px] pt-[9px]">
                    <span className="text-[13px] font-bold leading-[normal] text-ink">
                      {dayjs(previewDelivery.date).format("DD.MM.YYYY")}
                    </span>
                    {previewDelivery.invoiceNumber && (
                      <span className="text-[11.5px] leading-[normal] text-muted">
                        №{previewDelivery.invoiceNumber}
                      </span>
                    )}
                    <span className="ml-auto font-[ui-monospace,Menlo,monospace] text-[13px] font-bold leading-[normal] text-brand-deep-ink">
                      {formatPrice(previewDelivery.totalValueWithVat)}
                    </span>
                  </div>
                  <div
                    className={`${PREVIEW_GRID_CLASS} shrink-0 bg-fill px-3.5 py-[5px] text-[9.5px] font-semibold uppercase leading-[normal] tracking-[0.07em] text-muted`}
                  >
                    <span>{t("inventoryPage.colProductName")}</span>
                    <span className="text-right">
                      {t("inventoryPage.colQty")}
                    </span>
                    <span className="text-right">
                      {t("inventoryPage.colUnitPrice")}
                    </span>
                  </div>
                  <div className="min-h-0 overflow-y-auto">
                    {goods.map((item) => (
                      <div
                        key={item.productId}
                        className={`${PREVIEW_GRID_CLASS} items-baseline border-t border-shade-1 px-3.5 py-[5px]`}
                      >
                        <span className="truncate text-[12.5px] leading-[normal] text-ink">
                          {item.name}
                        </span>
                        <span className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[12.5px] leading-[normal] text-body">
                          {formatQtyInput(item.qty, decimalsOf(item.productId))}
                          {item.unit ? ` ${item.unit}` : ""}
                        </span>
                        <span className="text-right font-[ui-monospace,Menlo,monospace] text-[12.5px] font-semibold leading-[normal] text-ink">
                          {formatUnitPrice(item.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      }}
      className="w-full"
    />
  );
};

export default DeliveryTemplateSelect;
